import {
    WB_EntityRow,
    WB_PeriodRow,
    WB_PolicyAreaRow,
    WB_PolicyRow,
    WB_PolicyTermRow, WB_ProductConditionTypeRow,
    WB_SalesAreaRow
} from "../PA.Core/PAdbSchemaRows";
import {IEntity} from "./PA.MD.IF.Entity";
import {IDateRange, IMessage, IMessageProvider, IProgressProvider} from "../PA.Core/PA.Core.IF.Basic";
import {PAdbContext} from "../PA.Core/PAdbContext";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import { MessageProvider } from "../PA.Core/Basic/Messages";


export interface IBusinessPolicy {


    readonly bLoaded: boolean
    OnLoadComplete(): Promise<void>
    readonly LoadMessageProvider: IMessageProvider

    readonly PolicyArea: WB_PolicyAreaRow
    readonly Policy: WB_PolicyRow
    readonly Terms: {[wbPolicyTermId: number]: IBusinessPolicyTerm}
    readonly Procedure: IBusinessProcedureLine[],
    readonly Cubes: IBusinessPolicyCube[]
    readonly SalesAreas: WB_SalesAreaRow[]
    readonly Effective: IDateRange
}

export interface IBusinessPolicyTermType {
    readonly wbPolicyTermMId: number
    readonly name: string
    readonly businessType: string
    readonly bInverseValue: boolean
    readonly valueType: string
    readonly inputType: string
    readonly timeMode: string
    readonly bStored: boolean
    readonly storagePolicy: string
    readonly storageType: string
    readonly procedureRequirement: string

}

export interface IBusinessPolicyTerm {

    readonly wbPolicyTermId: number
    readonly name: string
    readonly type: IBusinessPolicyTermType

    readonly wbConditionMId: number
    readonly bInheritComponents: boolean //Comes from the Policy Procedure

    readonly bIsReserved: boolean
    readonly reservedTermId: string
}

export interface IBusinessProcedureLine {
    readonly wbPolicyPLRId: number
    readonly calcLineId: number
    readonly term: IBusinessPolicyTerm
    readonly calcType: "Add" | "Multiply"
    readonly calcRefLineId: number
    calcRefTerm: IBusinessPolicyTerm
    readonly bInheritComponents: boolean
    readonly multipleValuePolicy: "Sum" | "First"
}

export interface IBusinessPolicyCube {
    readonly Policy: IBusinessPolicy,

    readonly cubeWbId: number
    readonly cubeId: string
    readonly cubeName: string
    readonly cubeType: "Primary" | "Transformation"
    readonly cubeParent: IBusinessPolicy

    readonly bLoaded: boolean
    OnLoadComplete(): Promise<void>
    readonly LoadMessageProvider: IMessageProvider

    readonly CustomerEntityType: WB_EntityRow
    readonly ProductEntityType: WB_EntityRow
    readonly PeriodEntityType: WB_EntityRow
    getConditionType(wbPolicyTermId: number): WB_ProductConditionTypeRow



    readonly bProcessed: boolean
    Process(options?: IBusinessPolicyCubeProcessOptions): Promise<void>

    readonly ProcessProgressProvider: IProgressProvider
    readonly ProcessMessageProvider: IMessageProvider
    readonly ProcessedTerms: IBusinessPolicyTerm[]

    readonly Customers: {[wbId: number]: IEntity}
    readonly Products: {[wbId: number]: IEntity}
    readonly Periods : {[wbId: number]: IEntity}

    readonly CustomerProductScope: {[wbSalesAreaId: number]: {wbCustId: number, wbPrdId: number}[]}
    readonly PeriodScope: WB_PeriodRow[]

    readonly CellData: {[wbSalesAreaId: number]: {[wbCustId: number]: {[wbPrdId: number]: {[wbPeriodId: number]: ICubeCellData}}}}

}

export interface IBusinessPolicyCubeProcessOptions {
    bSkipWrite?: boolean,
    bSkipAuditLogging?: boolean,
    progressProvider?: ProgressProvider
    messageProvider?: MessageProvider,
    wbCustIds?: number[]
}

export interface IBusinessPolicyCubeWriterOptions {
    dbContext?: PAdbContext
    ProgressProvider?: ProgressProvider
    MessageProvider?: MessageProvider
    wbPolicyTermIds?: number[]
    wbSalesAreaId?: number
    wbCustIds?: number[]
}

export interface IBusinessPolicyCubeWriter {
    writeCellData(options: IBusinessPolicyCubeWriterOptions): Promise<void>
}

export interface ICubeCellData {
    wbCustId: number
    wbPrdId: number
    wbPeriodId: number
    wbSalesAreaId: number

    TermValues: {[wbPolicyTermId: number]: number}
    //ValueUOM: string,
    Errors: {[errorId: string]: string}
}


export interface IBusinessPolicyService {
    getPolicyProcessingStatus(wbPolicyId: number): Promise<IBusinessPolicyOperationStatus>
    startPolicyProcessing(wbPolicyId: number, options: IBusinessPolicyOperationStartOptions): Promise<void>
}

export enum BusinessPolicyProcessingStatus {
    Idle="Idle",
    NotStarted="NotStarted",
    Starting="Starting",
    Running="Running",
    Stopping="Stopping",
    Stopped="Stopped",
    RunningWithErrors="RunningErrors",
    CompletedWithErrors="CompletedErrors",
    CompletedSuccess="CompletedSuccess"
}


export interface IBusinessPolicyOperationStatus {
    Status: BusinessPolicyProcessingStatus,
    Options: IBusinessPolicyOperationStartOptions,
    StartTime: Date,
    EndTime: Date,
    ProgressProviderChannelGUID: string,
    Messages: IMessage[]
}

export interface IBusinessPolicyOperationStartOptions {

    dbContext?: PAdbContext
    ProgressProvider?: ProgressProvider
    MessageProvider?: MessageProvider

    bProcessCubes?: boolean     //Process
    wbCubeIds?: number[]
    bWait?: boolean
}

//--
// Constructor(PolicyArea, SalesOrg, CustomerEntity, ProductEntity, EffectiveDt)
//
export interface IPolicyProcedureOutput {
    termValues: {[wbPolicyTermId: number]: ITermValue}
    output: number
    errors: string[]
}

export interface ITermValue {
    prodCndId?: number
    conditionValue?: number
    conditionUOM?: string
    wbEvtSId?: number
    wbEvtSPGId?: number
    wbPrdGrpId?: number
    normalizedValue: number
    runningValue: number
    valueUOM: string
    lineId: number,
    wbPolicyPlrId: number,
    bHasSubTerms: boolean,
    subTerms: ITermValue[]
}

export interface IRunTimeValue {
        value: number
        // Allowance Properties
        wbEvtSId?: number
        wbEvtSPGId?: number
        wbPrdGrpId?: number
        rateUOM?: string
        rateBasis?: string
        roundingPolicy?: string
}
export interface IRunTimeOptions {
    values: {[wbPolicyTermId: number]: IRunTimeValue[]}
    componentValues?: {[wbPrdId: number]: {[wbPolicyTermId: number]: IRunTimeValue[]}},
    componentBOM?: {wbPrdId: number, componentQty: number, componentUOM?:  string}[],
    componentOutput?: {[wbPrdId: number]: IPolicyProcedureOutput},
    productUOM?: string
}

export interface IBusinessPolicyProcedureCalculator {
    readonly Policy: IBusinessPolicy
    readonly Cube: IBusinessPolicyCube
    readonly RuntimeTerms: IBusinessPolicyTerm[]
    OnLoadComplete(): Promise<void>
    readonly bLoaded: boolean
    readonly LoadMessageProvider: IMessageProvider

    evaluate(wbCustId: number, wbPrdId: number, EffDate: Date, runtimeOptions: IRunTimeOptions): Promise<IPolicyProcedureOutput>
    evaluateList(wbCustIds: number[], wbPrdIds: number[], EffDate: Date, runtimeOptions: {[wbCustId: number]: {[wbPrdId:number]: IRunTimeOptions}}): Promise<{[wbCustId: number]: {[wbPrdId:number]: IPolicyProcedureOutput}}>

}


